/**
 * PaypalReturnPage
 *
 * PayPal redirects here (return_url) after the buyer approves an order:
 * /invoice/paypal-return/:token?token=<paypal_order_id>&PayerID=...
 *
 * Note the two distinct "token"s: the route param `:token` is OUR payment-link
 * token, while the `token` query param is PayPal's ORDER id. We capture the
 * order server-side (the PAYMENT.CAPTURE.COMPLETED webhook is the backstop),
 * then show a confirmation.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import invoiceService from '../../services/invoiceService';
import './PublicInvoicePayment.css';

const PaypalReturnPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('token');

  const [state, setState] = useState('capturing'); // capturing | paid | error
  const [message, setMessage] = useState(null);
  const ranRef = useRef(false);

  const capture = useCallback(async () => {
    if (!orderId) {
      setState('error');
      setMessage('Missing PayPal order reference. If you were charged, please contact us.');
      return;
    }
    try {
      const res = await invoiceService.capturePaypalOrder(token, orderId);
      if (res.success && res.data?.data?.status === 'paid') {
        setState('paid');
        return;
      }
      setState('error');
      setMessage(res.data?.message || res.message || 'We could not confirm your payment.');
    } catch (err) {
      setState('error');
      setMessage('We could not confirm your payment. If you were charged, please contact us.');
    }
  }, [token, orderId]);

  useEffect(() => {
    // Guard against React 18 StrictMode double-invoke — capture only once.
    if (ranRef.current) return;
    ranRef.current = true;
    capture();
  }, [capture]);

  return (
    <div className="pip-container">
      <div className="pip-card pip-center">
        {state === 'capturing' ? (
          <>
            <h1 className="pip-title">Confirming your PayPal payment…</h1>
            <p className="pip-muted">Please wait, this only takes a moment.</p>
          </>
        ) : state === 'paid' ? (
          <>
            <div className="pip-check">✓</div>
            <h1 className="pip-title">Thank you — your payment was received</h1>
            <div className="pip-banner pip-banner-success">
              This invoice is now marked as <strong>Paid</strong>. A receipt has been sent by PayPal.
            </div>
            <Link to={`/invoice/pay/${token}`} className="pip-link">View invoice</Link>
          </>
        ) : (
          <>
            <h1 className="pip-title">Payment not confirmed</h1>
            <div className="pip-banner pip-banner-error">{message}</div>
            <Link to={`/invoice/pay/${token}`} className="pip-link">Back to invoice</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaypalReturnPage;
